import React, { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Search, Loader2, CheckSquare, Square, Plus, X } from "lucide-react";
import { toast } from "sonner";

const TIPOS = [
  { v: "troca",       label: "Troca" },
  { v: "aditivo",     label: "Aditivo" },
  { v: "devolucao",   label: "Devolução" },
  { v: "manutencao",  label: "Manutenção" },
];

export default function AbrirTicketModal({ open, onOpenChange, onCriado }) {
  const [step, setStep] = useState(1); // 1 cliente, 2 detalhes
  const [busca, setBusca] = useState("");
  const [opcoesCliente, setOpcoesCliente] = useState([]);
  const [buscandoCli, setBuscandoCli] = useState(false);
  const [cliente, setCliente] = useState(null);
  const [frota, setFrota] = useState([]);
  const [carregandoFrota, setCarregandoFrota] = useState(false);
  const [equipSelecionados, setEquipSelecionados] = useState([]);
  const [tipo, setTipo] = useState("troca");
  const [solicitante, setSolicitante] = useState("");
  const [previsao, setPrevisao] = useState("");
  const [endereco, setEndereco] = useState("");
  const [descricao, setDescricao] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep(1); setBusca(""); setOpcoesCliente([]); setCliente(null);
      setFrota([]); setEquipSelecionados([]); setTipo("troca");
      setSolicitante(""); setPrevisao(""); setEndereco(""); setDescricao("");
      setSalvando(false);
    }
  }, [open]);

  // busca clientes (debounce 300ms)
  useEffect(() => {
    if (step !== 1 || !open) return;
    const t = setTimeout(async () => {
      if (!busca || busca.length < 2) { setOpcoesCliente([]); return; }
      setBuscandoCli(true);
      try {
        const { data } = await api.get("/tickets-ops/busca", { params: { q: busca } });
        setOpcoesCliente(data.clientes || []);
      } catch (e) { /* */ } finally { setBuscandoCli(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [busca, step, open]);

  const selecionarCliente = useCallback(async (c) => {
    setCliente(c);
    setStep(2);
    setCarregandoFrota(true);
    try {
      const { data } = await api.get(`/tickets-ops/cliente/${c.id}/frota`);
      setFrota(data.items || []);
    } catch (e) { setFrota([]); }
    finally { setCarregandoFrota(false); }
  }, []);

  const toggleEquip = (eq) => {
    const exists = equipSelecionados.find((e) => e.id === eq.id);
    if (exists) {
      setEquipSelecionados(equipSelecionados.filter((e) => e.id !== eq.id));
    } else {
      setEquipSelecionados([...equipSelecionados, eq]);
    }
  };

  const criar = async () => {
    if (!cliente) { toast.error("Selecione um cliente"); return; }
    if (!tipo) { toast.error("Selecione o tipo"); return; }
    setSalvando(true);
    try {
      const body = {
        cliente_id: cliente.id,
        tipo,
        descricao,
        solicitante_nome: solicitante,
        previsao_atendimento: previsao || null,
        endereco_entrega: endereco,
        equipamentos: equipSelecionados.map((e) => ({
          id: e.id, serial: e.serial, placa: e.placa,
        })),
      };
      const { data } = await api.post("/tickets-ops", body);
      toast.success(`Ticket #${data.id.slice(0, 8)} criado`);
      onOpenChange(false);
      onCriado?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar ticket");
    } finally { setSalvando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="modal-abrir-ticket">
        <DialogHeader>
          <DialogTitle>Abrir Ticket {cliente && `· ${cliente.nome}`}</DialogTitle>
          <DialogDescription>
            {step === 1 ? "1. Selecione o cliente" : "2. Detalhes do ticket"}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase">Buscar cliente</Label>
              <div className="relative">
                <Search size={14} className="absolute left-2 top-3 text-slate-400" />
                <Input
                  className="pl-8"
                  placeholder="Nome, CNPJ ou código..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  data-testid="abrir-busca-cliente"
                  autoFocus
                />
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto border rounded">
              {buscandoCli && <div className="p-3 text-xs text-slate-500"><Loader2 className="inline animate-spin mr-1" size={12} /> Buscando...</div>}
              {!buscandoCli && opcoesCliente.length === 0 && busca.length >= 2 && (
                <div className="p-3 text-xs text-slate-500">Nenhum cliente encontrado.</div>
              )}
              {!buscandoCli && busca.length < 2 && (
                <div className="p-3 text-xs text-slate-500">Digite ao menos 2 caracteres.</div>
              )}
              {opcoesCliente.map((c) => (
                <button
                  key={c.id}
                  onClick={() => selecionarCliente(c)}
                  data-testid={`opcao-cliente-${c.id}`}
                  className="w-full text-left p-2 hover:bg-orange-50 border-b last:border-0 text-xs"
                >
                  <div className="font-semibold">{c.nome}</div>
                  <div className="text-slate-500 font-mono text-[10px]">{c.cnpj} · {c.uf || "—"} · {c.fonte}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && cliente && (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-xs flex items-center justify-between">
              <div>
                <strong>{cliente.nome}</strong> · {cliente.cnpj} · {cliente.uf}
              </div>
              <Button size="sm" variant="ghost" onClick={() => { setCliente(null); setStep(1); setFrota([]); setEquipSelecionados([]); }}>
                Trocar
              </Button>
            </div>

            <div>
              <Label className="text-xs uppercase">Tipo *</Label>
              <div className="flex gap-2 flex-wrap">
                {TIPOS.map((t) => (
                  <button
                    key={t.v}
                    type="button"
                    onClick={() => setTipo(t.v)}
                    data-testid={`tipo-${t.v}`}
                    className={`px-3 py-1.5 rounded border text-xs font-medium transition ${
                      tipo === t.v
                        ? "bg-orange-600 text-white border-orange-700"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-orange-50"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase">Equipamentos envolvidos</Label>
              {carregandoFrota && <div className="text-xs text-slate-500"><Loader2 className="inline animate-spin" size={12} /></div>}
              {!carregandoFrota && frota.length === 0 && (
                <div className="text-xs text-slate-400 italic p-2 border rounded">Cliente sem equipamentos vinculados.</div>
              )}
              {!carregandoFrota && frota.length > 0 && (
                <div className="max-h-40 overflow-y-auto border rounded">
                  {frota.map((eq) => {
                    const sel = !!equipSelecionados.find((e) => e.id === eq.id);
                    return (
                      <button
                        type="button"
                        key={eq.id}
                        onClick={() => toggleEquip(eq)}
                        data-testid={`equip-${eq.id}`}
                        className={`w-full text-left p-2 border-b last:border-0 text-xs flex items-center gap-2 ${sel ? "bg-emerald-50" : "hover:bg-slate-50"}`}
                      >
                        {sel ? <CheckSquare size={14} className="text-emerald-600" /> : <Square size={14} className="text-slate-400" />}
                        <span className="font-mono"><strong>{eq.serial}</strong> · {eq.placa || "—"}</span>
                        <Badge className="ml-auto text-[10px]">{eq.status}</Badge>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase">Solicitante</Label>
                <Input value={solicitante} onChange={(e) => setSolicitante(e.target.value)} placeholder="Nome do responsável" data-testid="solicitante" />
              </div>
              <div>
                <Label className="text-xs uppercase">Previsão</Label>
                <Input type="datetime-local" value={previsao} onChange={(e) => setPrevisao(e.target.value)} data-testid="previsao" />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase">Endereço de entrega</Label>
              <Input value={endereco} onChange={(e) => setEndereco(e.target.value)} placeholder="Rua, número, cidade/UF" data-testid="endereco" />
            </div>

            <div>
              <Label className="text-xs uppercase">Descrição / Observações</Label>
              <Textarea value={descricao} onChange={(e) => setDescricao(e.target.value)} rows={3} placeholder="Detalhes do chamado..." data-testid="descricao" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={salvando}>Cancelar</Button>
          {step === 2 && (
            <Button onClick={criar} disabled={salvando} className="bg-orange-600 hover:bg-orange-700" data-testid="confirmar-abrir-ticket">
              {salvando && <Loader2 size={12} className="animate-spin mr-2" />}
              <Plus size={14} className="mr-1" /> Abrir ticket
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
